/**
 * Event landing-page template — 06-networking ("Rooftop Mixer").
 *
 * Distinct CraftJS home-page document for the `networking` theme. Cloned from
 * the proven `webinar` template (same node structure / factories) but with the
 * rooftop-networking copy from
 *   TEMPLATES - LANDINPAGES/06-networking/sections/*.html (CRAFT_PROPS_HINT).
 *
 * Visual style: "glass-rooftop" — cool modern, glassy neutrals + sky-blue
 * accent, Poppins throughout (self-hosted via customCss), rounded-lg surfaces.
 * The page-wide colors/fonts come from NETWORKING_PRESET (applied via the
 * theme); only sections with their own surface (Hero/Testimonials over dark)
 * set explicit per-node colors here. Premium (P9 WS4-B4): sky-wash hero with
 * a rounded connective-dots motif + friendly-rise motion in customCss.
 *
 * Convention (mirrors temp.ts/webinar):
 *  - Section-level marketing copy goes through td() keys ("net*" prefix).
 *  - Repetitive structured child data (agenda rows, speakers, FAQ, …) also
 *    goes through td() keys (Wave 3b) — bold markup stays in code.
 *  - NO {{mustache}} placeholders anywhere — concrete static copy only.
 */
import { defaultRegistrationConsents, toCheckboxItems } from "@/lib/website/registrationConsents";
import {
  A,
  enc,
  pv,
  tc,
  btn,
  fi,
  acceptanceFi,
  scaffold,
  emptyFieldsGroup,
  PAD,
  DIST_CARD,
  BTN_FORM_SP,
  SEC_MARGIN,
  ACCEPTANCE_VISIBILITY_FILTER,
  type N,
  type TranslationFn,
} from "../temp";
import { EASE, decorLayer, fadeUpKeyframes, fontFaces, heroShell, motionGate, revealBlock } from "./premium-css";
import type { WebsiteThemeStylePreset } from "@/types/WebsiteTheme";
import {
  SAMPLE_SPEAKER_IMAGES,
  SAMPLE_SPONSOR_LOGOS,
} from "@/lib/website-builder/sampleContent";

// Networking-specific asset namespace (seeded under <CDN>/networking/…)
const W = `${A}/shared/templates/06-networking`;

const ACCENT = "#0369a1"; // deep sky blue — WCAG-safe on the #f0f9ff page for links/CTA/eyebrows (was #0ea5e9 @ 2.6:1)
const INK = "#0f172a"; // cool near-black headings
const MUTE = "#526077"; // muted slate body — 5.97:1 on #f0f9ff (was #64748b @ 4.46:1)
const DARK = "#0c1a2b"; // dark glassy section surface
const ON_DARK = "#cbd5e1"; // body text on dark
const ON_DARK_ACCENT = "#7dd3fc"; // eyebrow on dark
const SANS = "Poppins, 'Helvetica Neue', 'Segoe UI', Arial, sans-serif";

// Speaker/testimonial faces come from the shared sample rotation
// (lib/website-builder/sampleContent); the template's own `<family>/*`
// art stays what it is: heroes, details and gallery photography.
const LOGO = `${A}/shared/logo.svg`;

// ── Inline child data (i18n via td — Wave 3b) ─────────────────────────
// Structured child rows (agenda, FAQ, numbers, testimonials, speaker roles,
// blog teasers) come from td() keys so they localize with the site's
// primaryLanguage. Bold markup stays here in code; translations are plain text.

const NUMBERS_DATA = (td: TranslationFn) => [
  { num: "<b>142</b>", desc: td("netNum1Desc") },
  { num: "<b>18</b>", desc: td("netNum2Desc") },
  { num: "<b>63 %</b>", desc: td("netNum3Desc") },
  { num: "<b>486</b>", desc: td("netNum4Desc") },
];

const AGENDA_DATA = (td: TranslationFn) => [
  { time: `<b>${td("netAgendaRow1Time")}</b>`, title: td("netAgendaRow1Title"), who: td("netAgendaRow1Who"), desc: td("netAgendaRow1Desc") },
  { time: `<b>${td("netAgendaRow2Time")}</b>`, title: td("netAgendaRow2Title"), who: td("netAgendaRow2Who"), desc: td("netAgendaRow2Desc") },
  { time: `<b>${td("netAgendaRow3Time")}</b>`, title: td("netAgendaRow3Title"), who: td("netAgendaRow3Who"), desc: td("netAgendaRow3Desc") },
  { time: `<b>${td("netAgendaRow4Time")}</b>`, title: td("netAgendaRow4Title"), who: td("netAgendaRow4Who"), desc: td("netAgendaRow4Desc") },
  { time: `<b>${td("netAgendaRow5Time")}</b>`, title: td("netAgendaRow5Title"), who: td("netAgendaRow5Who"), desc: td("netAgendaRow5Desc") },
];

const SPEAKER_DATA = (td: TranslationFn) => [
  { name: "Mara Lindqvist", role: td("netSpeaker1Role") },
  { name: "Niklas Berger", role: td("netSpeaker2Role") },
  { name: "Aylin Demir", role: td("netSpeaker3Role") },
  { name: "Jonas Petrov", role: td("netSpeaker4Role") },
  { name: "Rania Khalil", role: td("netSpeaker5Role") },
  { name: "Tomás Vega", role: td("netSpeaker6Role") },
];

const TESTIMONIAL_DATA = (td: TranslationFn) => [
  { quote: td("netTestimonial1Quote"), name: "Lea Brennecke", role: td("netTestimonial1Role") },
  { quote: td("netTestimonial2Quote"), name: "Aylin Demir", role: td("netTestimonial2Role") },
  { quote: td("netTestimonial3Quote"), name: "Jonas Petrov", role: td("netTestimonial3Role") },
];

const BLOG_DATA = (td: TranslationFn) => [
  { topic: `<b>${td("netBlog1Topic")}</b>`, content: td("netBlog1Content") },
  { topic: `<b>${td("netBlog2Topic")}</b>`, content: td("netBlog2Content") },
  { topic: `<b>${td("netBlog3Topic")}</b>`, content: td("netBlog3Content") },
];

const FAQ_DATA = (td: TranslationFn) => [
  { q: `<b>${td("netFaq1Question")}</b>`, a: td("netFaq1Answer") },
  { q: `<b>${td("netFaq2Question")}</b>`, a: td("netFaq2Answer") },
  { q: `<b>${td("netFaq3Question")}</b>`, a: td("netFaq3Answer") },
  { q: `<b>${td("netFaq4Question")}</b>`, a: td("netFaq4Answer") },
  { q: `<b>${td("netFaq5Question")}</b>`, a: td("netFaq5Answer") },
  { q: `<b>${td("netFaq6Question")}</b>`, a: td("netFaq6Answer") },
];

// ── Networking-specific child factories ───────────────────────────────

function numbersChild(parent: string, i: number, td: TranslationFn): N {
  const d = NUMBERS_DATA(td)[i];
  return {
    type: { resolvedName: "NumbersChild" }, isCanvas: false,
    props: {
      isVisible: true,
      isVisibleNumber: true, textNumber: d.num, fontSizeNumber: "text-4xl", textAlignNumber: "center",
      isVisibleDescription: true, textDescription: d.desc, fontSizeDescription: "text-sm", textAlignDescription: "center",
    },
    displayName: "NumbersChild", custom: { icon: "IconNumber" },
    parent, hidden: false, nodes: [], linkedNodes: {},
  };
}

function agendaChild(parent: string, i: number, td: TranslationFn): N {
  const d = AGENDA_DATA(td)[i];
  return {
    type: { resolvedName: "AgendaChild" }, isCanvas: false,
    props: {
      isVisibleText1: true, textText1: d.time, fontSizeText1: "text-sm", textAlignText1: "left",
      isVisibleText2: true, textText2: d.title, fontSizeText2: "text-sm", textAlignText2: "left",
      isVisibleText3: true, textText3: d.who, fontSizeText3: "text-sm", textAlignText3: "left",
      isVisibleText: true, textText: d.desc, fontSizeText: "text-sm", textAlignText: "left", border: true,
    },
    displayName: "AgendaChild", custom: { icon: "IconCalendar" },
    parent, hidden: false, nodes: [], linkedNodes: {},
  };
}

function speakerChild(parent: string, i: number, td: TranslationFn): N {
  const d = SPEAKER_DATA(td)[i];
  return {
    type: { resolvedName: "SpeakerChild" }, isCanvas: false,
    props: {
      isVisible: true, imageUrl: SAMPLE_SPEAKER_IMAGES[i % SAMPLE_SPEAKER_IMAGES.length], imageBottomBackground: "transparent",
      isVisibleName: true, textName: d.name, fontSizeName: "text-lg", textAlignName: "left",
      isVisibleOccupation: true, textOccupation: d.role, fontSizeOccupation: "text-sm", textAlignOccupation: "left",
      icons: [{ icon: "IconBrandLinkedin" }, { icon: "IconMail" }], isVisibleIcon: true,
    },
    displayName: "SpeakerChild", custom: { icon: "IconMicrophone" },
    parent, hidden: false, nodes: [], linkedNodes: {},
  };
}

function testimonialChild(parent: string, i: number, td: TranslationFn): N {
  const d = TESTIMONIAL_DATA(td)[i];
  return {
    type: { resolvedName: "TestimonialsChild" }, isCanvas: false,
    props: {
      isVisible: true, imageUrl: SAMPLE_SPONSOR_LOGOS[i % SAMPLE_SPONSOR_LOGOS.length].src,
      imageUrlBottom: SAMPLE_SPEAKER_IMAGES[i % SAMPLE_SPEAKER_IMAGES.length],
      isVisibleImageTop: false, imageBottomBackground: "transparent", isVisibleImageBottom: false,
      isVisibleContent: true, textContent: d.quote, fontSizeContent: "text-md", textAlignContent: "left", fontColorContent: ON_DARK,
      isVisibleName: true, textName: d.name, fontSizeName: "text-lg", textAlignName: "left", fontColorName: "#ffffff",
      isVisibleOccupation: true, textOccupation: d.role, fontSizeOccupation: "text-sm", textAlignOccupation: "left", fontColorOccupation: ON_DARK,
    },
    displayName: "TestimonialsChild", custom: { icon: "IconThumbUp" },
    parent, hidden: false, nodes: [], linkedNodes: {},
  };
}

function blogChild(parent: string, i: number, td: TranslationFn): N {
  const d = BLOG_DATA(td)[i];
  return {
    type: { resolvedName: "BlogChild" }, isCanvas: false,
    props: {
      iconAlignment: "left", isVisible: true,
      isVisibleTopic: true, textTopic: d.topic, fontSizeTopic: "text-lg", textAlignTopic: "left",
      isVisibleContent: true, textContent: d.content, fontSizeContent: "text-md", textAlignContent: "left",
      isVisibleButton: false, size: "small", text: td("learnMore"), underline: false, border: true,
    },
    displayName: "BlogChild", custom: { icon: "IconArticle" },
    parent, hidden: false, nodes: [], linkedNodes: {},
  };
}

function faqChild(parent: string, i: number, td: TranslationFn): N {
  const d = FAQ_DATA(td)[i];
  return {
    type: { resolvedName: "FaQChild" }, isCanvas: false,
    props: {
      isVisibleQuestion: true, textQuestion: d.q, fontSizeQuestion: "text-sm", textAlignQuestion: "left",
      isVisibleAnswer: true, textAnswer: d.a, fontSizeAnswer: "text-sm", textAlignAnswer: "left", border: true,
    },
    displayName: "FaQChild", custom: { icon: "IconMessageQuestion" },
    parent, hidden: false, nodes: [], linkedNodes: {},
  };
}

const GALLERY_IMG = [
  `${W}/hero-einladung.jpg`, `${W}/detail-cocktails.jpg`, `${W}/detail-cheers.jpg`,
  `${W}/hero-treatment.jpg`, `${W}/detail-skyline.jpg`, `${W}/hero-absage.jpg`,
];

function galleryImage(parent: string, i: number): N {
  return {
    type: { resolvedName: "ImageChild" }, isCanvas: false,
    props: { src: GALLERY_IMG[i % GALLERY_IMG.length], isVisible: true, aspectRatio: "aspect-video", displayName: "ImageChild", width: "w-full", canDuplicate: true },
    displayName: "ImageChild", custom: { icon: "IconSection" },
    parent, hidden: false, nodes: [], linkedNodes: {},
  };
}

// ── Theme CSS (P9 WS4-B4) ─────────────────────────────────────────────
//
// Marker comment first so a later user edit is distinguishable from the
// pristine preset. Composed from the shared premium-css builders (WS3).
// The hero keeps its light sky sheet + photo (node props); the css layers the
// glass-rooftop treatment on top: a soft sky gradient wash, a loose cluster of
// rounded connective dots in the template's own blues, a grounding dot-grid
// patch, uppercase letterspaced kickers, and the self-hosted Poppins faces so
// the preset's stack actually resolves.
const CUSTOM_CSS = `/* theme:networking */
${fontFaces([
  { family: "Poppins", file: "poppins-regular", weightRange: "400" },
  { family: "Poppins", file: "poppins-semibold", weightRange: "600" },
  { family: "Poppins", file: "poppins-bold", weightRange: "700" },
])}

/* Rooftop hero — clip + isolate the sky wash + dot ornament layers */
#net_hero {
${heroShell}
  background-image: linear-gradient(170deg, #e0f2fe 0%, rgba(224, 242, 254, 0.45) 44%, rgba(240, 249, 255, 0) 100%);
}

/* Connective-dots motif: a loose cluster of rounded nodes, top right,
   in the template's own blues (sky, deep sky, indigo) */
#net_hero::before {
${decorLayer}
  top: 8%;
  right: 5%;
  width: 180px;
  height: 140px;
  background-image:
    radial-gradient(circle 26px at 24% 32%, rgba(3, 105, 161, 0.28) 99%, transparent 100%),
    radial-gradient(circle 16px at 62% 70%, rgba(14, 165, 233, 0.34) 99%, transparent 100%),
    radial-gradient(circle 10px at 86% 22%, rgba(99, 102, 241, 0.30) 99%, transparent 100%);
}

/* Dot-grid patch grounding the lower-left corner (rounded network dots) */
#net_hero::after {
${decorLayer}
  left: 4%;
  bottom: 8%;
  width: 150px;
  height: 90px;
  background-image: radial-gradient(circle, rgba(3, 105, 161, 0.22) 2px, transparent 2.5px);
  background-size: 18px 18px;
}

/* Uppercase letterspaced kickers (the text-xs eyebrows in each section) */
#net_hero .text-xs, #net_numbers .text-xs, #net_agenda .text-xs, #net_speaker .text-xs,
#net_text .text-xs, #net_image .text-xs, #net_countdown .text-xs, #net_gallery .text-xs,
#net_video .text-xs, #net_testimonials .text-xs, #net_blog .text-xs, #net_faq .text-xs,
#net_map .text-xs, #net_contact .text-xs {
  text-transform: uppercase;
  letter-spacing: 0.22em;
}

${motionGate(`${fadeUpKeyframes("net", 18)}
  @keyframes netRise {
    from { opacity: 0; transform: translateY(26px); }
    to { opacity: 1; transform: translateY(0); }
  }
  @keyframes netDotsIn {
    from { opacity: 0; }
    to { opacity: 1; }
  }

  /* Friendly rise: the deck lifts warmly, the dot clusters settle in after */
  #net_hero { animation: netRise 0.8s ${EASE} both; }
  #net_hero::before { animation: netDotsIn 1.4s ${EASE} 0.35s both; }
  #net_hero::after { animation: netDotsIn 1.4s ${EASE} 0.55s both; }

${revealBlock("net", ["numbers", "datainput", "agenda", "speaker", "text", "image", "countdown", "gallery", "video", "testimonials", "blog", "faq", "map", "contact"], 30)}`)}
`;

// ── Style preset ──────────────────────────────────────────────────────

export const networkingPreset: WebsiteThemeStylePreset = {
  heroImage: `${W}/hero-einladung.jpg`,
  logoSrc: LOGO,
  colorPalette: { primary: ACCENT, secondary: "#6366f1", background: "#f0f9ff" },
  fonts: { header: SANS, body: SANS },
  rounding: "rounded-lg",
  recommendedSize: "medium",
  bodyTheme: { fontColor: MUTE, fontSize: "16px", layoutOption: "full-width" },
  headingsTheme: {
    h1: { fontFamily: SANS, fontSize: "48px", fontColor: INK },
    h2: { fontFamily: SANS, fontSize: "28px", fontColor: INK },
  },
  buttonsTheme: {
    primary: { bgColor: ACCENT, bgHoverColor: "#0284c7", borderColor: ACCENT, borderHoverColor: "#0284c7", fontColor: "#ffffff", fontHoverColor: "#ffffff", fontSize: "16px", fontHoverSize: "16px", borderRadius: "rounded-lg", hoverBorderRadius: "rounded-lg" },
    secondary: { bgColor: "transparent", bgHoverColor: ACCENT, borderColor: ACCENT, borderHoverColor: ACCENT, fontColor: ACCENT, fontHoverColor: "#ffffff", fontSize: "16px", fontHoverSize: "16px", borderRadius: "rounded-lg", hoverBorderRadius: "rounded-lg" },
  },
  hyperlinkTheme: { fontColor: ACCENT, fontHoverColor: "#0284c7", underline: false },
  formInputTheme: { labelFontColor: INK, inputFontColor: INK, backgroundColor: "#ffffff", border: true, borderColor: "#bae6fd", borderRadius: "rounded-lg", borderStyle: "border-solid", borderWidth: "border", borderSides: [], fontFamily: SANS, fontSize: "text-sm", errorColor: "#DC2626" },
  customCss: CUSTOM_CSS,
};

// ── Template ──────────────────────────────────────────────────────────

export const networking = (td: TranslationFn): string => {
  const B = "net_body";

  const HERO = "net_hero";
  const NUMBERS = "net_numbers";
  const AGENDA = "net_agenda";
  const SPEAKER = "net_speaker";
  const TEXT = "net_text";
  const IMAGE = "net_image";
  const COUNTDOWN = "net_countdown";
  const GALLERY = "net_gallery";
  const VIDEO = "net_video";
  const TESTI = "net_testimonials";
  const BLOG = "net_blog";
  const FAQ = "net_faq";
  const MAP = "net_map";
  const DI = "net_datainput";
  const CONTACT = "net_contact";

  return enc({
    ...scaffold(
      { ab: "net_ab", abs: "net_abs", body: B, ft: "net_ft", fts: "net_fts" },
      [HERO, NUMBERS, DI, AGENDA, SPEAKER, TEXT, IMAGE, COUNTDOWN, GALLERY, VIDEO, TESTI, BLOG, FAQ, MAP, CONTACT],
      false, { background: "bg-white", padding: "p-4", borderRadius: "rounded-lg" },
    ),

    // ── Hero (image right, dark text on glassy light) ──
    [HERO]: {
      type: { resolvedName: "Hero" }, isCanvas: false,
      props: {
        sectionHeight: 90, contentAlignment: "center", imageLayout: "right", layoutWidth: "50%", inversed: true,
        padding: PAD, distButtons: "gap-3 wb-md:gap-4", distContentImage: "gap-6 wb-md:gap-10 wb-lg:gap-16", distHeadlineHeader: "mb-3 wb-md:mb-4", distHeaderText: "mb-4 wb-md:mb-6", distTextButtons: "gap-6 wb-md:gap-8", buttonAlignment: "flex-row", isVisible: true,
        isVisibleHeadline: true, textHeadline: td("netHeroEyebrow"), fontSizeHeadline: "text-xs", textAlignHeadline: "left", headlineColor: ACCENT,
        isVisibleHeader: true, textHeader: td("netHeroHeader"), fontSizeHeader: "text-6xl", textAlignHeader: "left", headerColor: INK,
        isVisibleText: true, textText: td("netHeroText"), fontSizeText: "text-lg", textAlignText: "left", textColor: MUTE,
        secondaryButtonSize: "medium", secondaryButtonText: td("netHeroSecondaryButton"), isVisibleSecondaryButton: true,
        secondaryButtonBorder: true, secondaryButtonUnderline: false,
        primaryButtonSize: "medium", primaryButtonText: td("netHeroPrimaryButton"), isVisiblePrimaryButton: true,
        primaryButtonBorder: true, primaryButtonUnderline: false,
        backgroundType: "image", imageRounded: true, backgroundOverlayColor: "transparent",
        imageSrc: `${W}/hero-einladung.jpg`, backgroundColor: "#f0f9ff",
      },
      displayName: "Hero", custom: { icon: "IconSlideshow", preview: pv("Hero-preview") },
      parent: B, hidden: false, nodes: [],
      linkedNodes: { headline: "net_hero_eb", header: "net_hero_hd", text: "net_hero_tx", buttonOne: "net_hero_b1", buttonTwo: "net_hero_b2" },
    },
    net_hero_eb: tc(HERO, { isVisible: true, text: td("netHeroEyebrow"), fontSize: "text-xs", textAlign: "left", displayName: "headline", color: ACCENT, header: true }),
    net_hero_hd: tc(HERO, { isVisible: true, text: td("netHeroHeader"), fontSize: "text-6xl", textAlign: "left", displayName: "header", color: INK, header: true }),
    net_hero_tx: tc(HERO, { isVisible: true, text: td("netHeroText"), fontSize: "text-lg", textAlign: "left", displayName: "text", color: MUTE, header: false }),
    net_hero_b1: btn(HERO, { type: "secondary", text: td("netHeroSecondaryButton"), displayName: "buttonOne" }),
    net_hero_b2: btn(HERO, { type: "primary", linkType: "internal", linkUrl: "#" + DI, text: td("netHeroPrimaryButton"), displayName: "buttonTwo" }),

    // ── Numbers ──
    [NUMBERS]: {
      type: { resolvedName: "Numbers" }, isCanvas: true,
      props: {
        sectionHeight: 60, contentAlignment: "center", sectionMargin: SEC_MARGIN, gridColumns: "4",
        padding: PAD, distHeadlineHeader: "mb-3 wb-md:mb-4", distHeaderText: "mb-4 wb-md:mb-6", distTextEntries: "gap-8 wb-md:gap-12", distEntries: "gap-5 wb-md:gap-6", isVisible: true, layoutWidth: "100%",
        isVisibleHeadline: false, textHeadline: td("netNumbersHeader"), fontSizeHeadline: "text-lg", textAlignHeadline: "center",
        isVisibleHeader: true, textHeader: td("netNumbersHeader"), fontSizeHeader: "text-3xl", textAlignHeader: "center",
        isVisibleText: true, textText: td("netNumbersText"), fontSizeText: "text-md", textAlignText: "center",
      },
      displayName: "Numbers", custom: { icon: "Icon123", preview: pv("NumbersPreview"), allowedComponents: ["NumbersChild"] },
      parent: B, hidden: false, nodes: ["net_num1", "net_num2", "net_num3", "net_num4"],
      linkedNodes: { headline: "net_num_eb", header: "net_num_hd", text: "net_num_tx" },
    },
    net_num_eb: tc(NUMBERS, { isVisible: false, text: td("netNumbersHeader"), fontSize: "text-lg", textAlign: "center", displayName: "headline", header: true }),
    net_num_hd: tc(NUMBERS, { isVisible: true, text: td("netNumbersHeader"), fontSize: "text-3xl", textAlign: "center", displayName: "header", header: true }),
    net_num_tx: tc(NUMBERS, { isVisible: true, text: td("netNumbersText"), fontSize: "text-md", textAlign: "center", displayName: "text", header: false }),
    net_num1: numbersChild(NUMBERS, 0, td),
    net_num2: numbersChild(NUMBERS, 1, td),
    net_num3: numbersChild(NUMBERS, 2, td),
    net_num4: numbersChild(NUMBERS, 3, td),

    // ── Agenda ──
    [AGENDA]: {
      type: { resolvedName: "Agenda" }, isCanvas: true,
      props: {
        padding: PAD, distHeadlineHeader: "mb-3 wb-md:mb-4", distHeaderText: "mb-4 wb-md:mb-6", distTextEntries: "gap-8 wb-md:gap-12", distEntries: "space-y-3 wb-md:space-y-4", sectionMargin: SEC_MARGIN, isVisible: true, layoutWidth: "100%",
        isVisibleHeadline: false, textHeadline: td("netAgendaHeader"), fontSizeHeadline: "text-lg", textAlignHeadline: "center",
        isVisibleHeader: true, textHeader: td("netAgendaHeader"), fontSizeHeader: "text-3xl", textAlignHeader: "center",
        isVisibleText: true, textText: td("netAgendaText"), fontSizeText: "text-md", textAlignText: "center",
      },
      displayName: "Agenda", custom: { icon: "IconListCheck", preview: pv("AgendaPreview"), allowedComponents: ["AgendaChild"] },
      parent: B, hidden: false, nodes: ["net_ag1", "net_ag2", "net_ag3", "net_ag4", "net_ag5"],
      linkedNodes: { headline: "net_ag_eb", header: "net_ag_hd", text: "net_ag_tx" },
    },
    net_ag_eb: tc(AGENDA, { isVisible: false, text: td("netAgendaHeader"), fontSize: "text-lg", textAlign: "center", displayName: "headline", header: true }),
    net_ag_hd: tc(AGENDA, { isVisible: true, text: td("netAgendaHeader"), fontSize: "text-3xl", textAlign: "center", displayName: "header", header: true }),
    net_ag_tx: tc(AGENDA, { isVisible: true, text: td("netAgendaText"), fontSize: "text-md", textAlign: "center", displayName: "text", header: false }),
    net_ag1: agendaChild(AGENDA, 0, td),
    net_ag2: agendaChild(AGENDA, 1, td),
    net_ag3: agendaChild(AGENDA, 2, td),
    net_ag4: agendaChild(AGENDA, 3, td),
    net_ag5: agendaChild(AGENDA, 4, td),

    // ── Speaker (hidden — no portrait library) ──
    [SPEAKER]: {
      type: { resolvedName: "Speaker" }, isCanvas: true,
      props: {
        padding: PAD, distHeadlineHeader: "mb-3 wb-md:mb-4", distHeaderText: "mb-4 wb-md:mb-6", distTextSpeakers: "gap-8 wb-md:gap-12", distSpeakers: "gap-5 wb-md:gap-6", sectionMargin: SEC_MARGIN, isVisible: false, gridColumns: "3", layoutWidth: "100%",
        isVisibleHeadline: false, textHeadline: td("netSpeakerHeader"), fontSizeHeadline: "text-lg", textAlignHeadline: "center",
        isVisibleHeader: true, textHeader: td("netSpeakerHeader"), fontSizeHeader: "text-3xl", textAlignHeader: "center",
        isVisibleText: true, textText: td("netSpeakerText"), fontSizeText: "text-md", textAlignText: "center",
      },
      displayName: "Speaker", custom: { icon: "IconUserStar", preview: pv("Speaker-preview"), allowedComponents: ["SpeakerChild"] },
      parent: B, hidden: false, nodes: ["net_sp1", "net_sp2", "net_sp3", "net_sp4", "net_sp5", "net_sp6"],
      linkedNodes: { headline: "net_sp_eb", header: "net_sp_hd", text: "net_sp_tx" },
    },
    net_sp_eb: tc(SPEAKER, { isVisible: false, text: td("netSpeakerHeader"), fontSize: "text-lg", textAlign: "center", displayName: "headline", header: true }),
    net_sp_hd: tc(SPEAKER, { isVisible: true, text: td("netSpeakerHeader"), fontSize: "text-3xl", textAlign: "center", displayName: "header", header: true }),
    net_sp_tx: tc(SPEAKER, { isVisible: true, text: td("netSpeakerText"), fontSize: "text-md", textAlign: "center", displayName: "text", header: false }),
    net_sp1: speakerChild(SPEAKER, 0, td),
    net_sp2: speakerChild(SPEAKER, 1, td),
    net_sp3: speakerChild(SPEAKER, 2, td),
    net_sp4: speakerChild(SPEAKER, 3, td),
    net_sp5: speakerChild(SPEAKER, 4, td),
    net_sp6: speakerChild(SPEAKER, 5, td),

    // ── Text (editorial note from host) ──
    [TEXT]: {
      type: { resolvedName: "TextSection" }, isCanvas: false,
      props: {
        buttonAlign: "center", padding: PAD, distHeadlineHeader: "mb-3 wb-md:mb-4", distHeaderText: "mb-4 wb-md:mb-6", distTextButtons: "gap-6 wb-md:gap-8", distButtons: "gap-3 wb-md:gap-4", sectionMargin: SEC_MARGIN, isVisible: true, layoutWidth: "100%",
        isVisibleHeadline: false, textHeadline: td("netTextHeader"), fontSizeHeadline: "text-lg", textAlignHeadline: "center",
        isVisibleHeader: true, textHeader: td("netTextHeader"), fontSizeHeader: "text-4xl", textAlignHeader: "center",
        isVisibleText: true, textText: td("netTextBody"), fontSizeText: "text-md", textAlignText: "center",
        secondaryButtonSize: "medium", secondaryButtonText: td("netTextSecondaryButton"), isVisibleSecondaryButton: true,
        secondaryButtonBorder: true, secondaryButtonUnderline: false, secondaryButtonAlign: "center",
        primaryButtonSize: "medium", primaryButtonText: td("netTextPrimaryButton"), isVisiblePrimaryButton: true,
        primaryButtonBorder: true, primaryButtonUnderline: false, primaryButtonAlign: "center",
      },
      displayName: "Text", custom: { icon: "IconAlignJustified", preview: pv("textPreview") },
      parent: B, hidden: false, nodes: [],
      linkedNodes: { headline: "net_tx_eb", header: "net_tx_hd", text: "net_tx_tx", buttonOne: "net_tx_b1", buttonTwo: "net_tx_b2" },
    },
    net_tx_eb: tc(TEXT, { isVisible: false, text: td("netTextHeader"), fontSize: "text-lg", textAlign: "center", displayName: "headline", header: true }),
    net_tx_hd: tc(TEXT, { isVisible: true, text: td("netTextHeader"), fontSize: "text-4xl", textAlign: "center", displayName: "header", header: true }),
    net_tx_tx: tc(TEXT, { isVisible: true, text: td("netTextBody"), fontSize: "text-md", textAlign: "center", displayName: "text", header: false }),
    net_tx_b1: btn(TEXT, { type: "secondary", text: td("netTextSecondaryButton"), displayName: "buttonOne" }),
    net_tx_b2: btn(TEXT, { type: "primary", text: td("netTextPrimaryButton"), displayName: "buttonTwo" }),

    // ── Image (the bar, image left / text right) ──
    [IMAGE]: {
      type: { resolvedName: "Image" }, isCanvas: false,
      props: {
        sectionHeight: 70, contentAlignment: "center", sectionMargin: SEC_MARGIN, inversed: true,
        padding: PAD, distHeadlineHeader: "mb-3 wb-md:mb-4", distHeaderText: "mb-4 wb-md:mb-6", distTextButton: "mb-6 wb-md:mb-8", distContentImage: "gap-6 wb-md:gap-10 wb-lg:gap-16", isVisible: true,
        isVisibleHeadline: false, textHeadline: td("netImageHeader"), fontSizeHeadline: "text-lg", textAlignHeadline: "left",
        isVisibleHeader: true, textHeader: td("netImageHeader"), fontSizeHeader: "text-3xl", textAlignHeader: "left",
        isVisibleText: true, textText: td("netImageText"), fontSizeText: "text-md", textAlignText: "left",
        buttonSize: "medium", buttonText: td("netImageButton"), isVisibleButton: true, buttonAlign: "left",
        buttonBorder: true, buttonUnderline: false,
        imageRounded: "rounded-lg", layoutWidth: "50%",
        isVisibleImage: true, imageSrc: `${W}/detail-cocktails.jpg`, imageAspectRatio: "aspect-[4/5]",
      },
      displayName: "Image", custom: { icon: "IconSection", preview: pv("Image-preview") },
      parent: B, hidden: false, nodes: [],
      linkedNodes: { headline: "net_im_eb", header: "net_im_hd", text: "net_im_tx", button: "net_im_btn", image: "net_im_img" },
    },
    net_im_eb: tc(IMAGE, { isVisible: false, text: td("netImageHeader"), fontSize: "text-lg", textAlign: "left", displayName: "headline", header: true }),
    net_im_hd: tc(IMAGE, { isVisible: true, text: td("netImageHeader"), fontSize: "text-3xl", textAlign: "left", displayName: "header", header: true }),
    net_im_tx: tc(IMAGE, { isVisible: true, text: td("netImageText"), fontSize: "text-md", textAlign: "left", displayName: "text", header: false }),
    net_im_btn: btn(IMAGE, { text: td("netImageButton"), displayName: "button" }),
    net_im_img: {
      type: { resolvedName: "ImageChild" }, isCanvas: false,
      props: { src: `${W}/detail-cocktails.jpg`, isVisible: true, aspectRatio: "aspect-[4/5]", displayName: "ImageChild", width: "w-full wb-lg:w-1/2", canDuplicate: false },
      displayName: "ImageChild", custom: { icon: "IconSection" },
      parent: IMAGE, hidden: false, nodes: [], linkedNodes: {},
    },

    // ── Countdown ──
    [COUNTDOWN]: {
      type: { resolvedName: "Countdown" }, isCanvas: false,
      props: {
        sectionHeight: 40, contentAlignment: "center", sectionMargin: SEC_MARGIN, layoutWidth: "50%",
        padding: PAD, distHeadlineHeader: "mb-3 wb-md:mb-4", distHeaderText: "mb-4 wb-md:mb-6", distTextButton: "mb-6 wb-md:mb-8", distButtonCountdown: "gap-6 wb-md:gap-10 wb-lg:gap-16", isVisible: true, buttonAlign: "left",
        isVisibleHeadline: true, textHeadline: td("netCountdownEyebrow"), fontSizeHeadline: "text-xs", textAlignHeadline: "left",
        isVisibleHeader: true, textHeader: td("netCountdownHeader"), fontSizeHeader: "text-4xl", textAlignHeader: "left",
        isVisibleText: true, textText: td("netCountdownText"), fontSizeText: "text-base", textAlignText: "left",
        buttonSize: "medium", buttonText: td("netCountdownButton"), isVisibleButton: true, buttonBorder: true, buttonUnderline: false,
        isVisiblCountdown: true, countdownSizeDate: "text-5xl", countdownFontTypeDate: SANS, countdownFontColorDate: INK,
        countdownSize: "text-base", countdownFontColor: "#64748b", countdownBorderColor: "#bae6fd",
      },
      displayName: "Countdown", custom: { icon: "IconAlarm", preview: pv("Countdown-preview") },
      parent: B, hidden: false, nodes: [],
      linkedNodes: { headline: "net_cd_eb", header: "net_cd_hd", text: "net_cd_tx", button: "net_cd_btn" },
    },
    net_cd_eb: tc(COUNTDOWN, { isVisible: true, text: td("netCountdownEyebrow"), fontSize: "text-xs", textAlign: "left", displayName: "headline", color: ACCENT, header: true }),
    net_cd_hd: tc(COUNTDOWN, { isVisible: true, text: td("netCountdownHeader"), fontSize: "text-4xl", textAlign: "left", displayName: "header", color: INK, header: true }),
    net_cd_tx: tc(COUNTDOWN, { isVisible: true, text: td("netCountdownText"), fontSize: "text-base", textAlign: "left", displayName: "text", color: MUTE, header: false }),
    net_cd_btn: btn(COUNTDOWN, { text: td("netCountdownButton"), displayName: "ButtonChild" }),

    // ── Gallery (rooftop, hidden — no photo library) ──
    [GALLERY]: {
      type: { resolvedName: "Gallery" }, isCanvas: true,
      props: {
        padding: PAD, distHeadlineHeader: "mb-3 wb-md:mb-4", distHeaderText: "mb-4 wb-md:mb-6", distTextButtons: "gap-6 wb-md:gap-8", distButtons: "gap-3 wb-md:gap-4", distButtonGallery: "gap-3 wb-md:gap-4", distEntries: "gap-5 wb-md:gap-6", sectionMargin: SEC_MARGIN, isVisible: false, buttonAlign: "center", gridColumns: "4", layoutWidth: "100%",
        isVisibleHeadline: false, textHeadline: td("netGalleryHeader"), fontSizeHeadline: "text-lg", textAlignHeadline: "center",
        isVisibleHeader: true, textHeader: td("netGalleryHeader"), fontSizeHeader: "text-3xl", textAlignHeader: "center",
        isVisibleText: false, textText: td("netGalleryHeader"), fontSizeText: "text-md", textAlignText: "center",
        secondaryButtonSize: "medium", secondaryButtonText: td("netGallerySecondaryButton"), isVisibleSecondaryButton: true,
        secondaryButtonBorder: true, secondaryButtonUnderline: false,
        primaryButtonSize: "medium", primaryButtonText: td("netGalleryPrimaryButton"), isVisiblePrimaryButton: true,
        primaryButtonBorder: true, primaryButtonUnderline: false,
      },
      displayName: "Gallery", custom: { icon: "IconPhoto", preview: pv("Gallery-preview"), allowedComponents: ["ImageChild"] },
      parent: B, hidden: false,
      nodes: ["net_g1", "net_g2", "net_g3", "net_g4", "net_g5", "net_g6"],
      linkedNodes: { headline: "net_g_eb", header: "net_g_hd", text: "net_g_tx", secondary: "net_g_b2", primary: "net_g_b1" },
    },
    net_g_eb: tc(GALLERY, { isVisible: false, text: td("netGalleryHeader"), fontSize: "text-lg", textAlign: "center", displayName: "headline", header: true }),
    net_g_hd: tc(GALLERY, { isVisible: true, text: td("netGalleryHeader"), fontSize: "text-3xl", textAlign: "center", displayName: "header", header: true }),
    net_g_tx: tc(GALLERY, { isVisible: false, text: td("netGalleryHeader"), fontSize: "text-md", textAlign: "center", displayName: "text", header: false }),
    net_g_b2: btn(GALLERY, { type: "secondary", text: td("netGallerySecondaryButton"), displayName: "ButtonChild" }),
    net_g_b1: btn(GALLERY, { text: td("netGalleryPrimaryButton"), displayName: "ButtonChild" }),
    net_g1: galleryImage(GALLERY, 0),
    net_g2: galleryImage(GALLERY, 1),
    net_g3: galleryImage(GALLERY, 2),
    net_g4: galleryImage(GALLERY, 3),
    net_g5: galleryImage(GALLERY, 4),
    net_g6: galleryImage(GALLERY, 5),

    // ── Video (aftermovie) — present but hidden by default ──
    [VIDEO]: {
      type: { resolvedName: "Video" }, isCanvas: false,
      props: {
        sectionHeight: 70, contentAlignment: "center", sectionMargin: SEC_MARGIN, layoutWidth: "50%",
        padding: PAD, distHeadlineHeader: "mb-3 wb-md:mb-4", distHeaderText: "mb-4 wb-md:mb-6", distTextButton: "mb-6 wb-md:mb-8", distContentVideo: "gap-6 wb-md:gap-10 wb-lg:gap-16", isVisible: false,
        isVisibleHeadline: false, textHeadline: td("netVideoHeader"), fontSizeHeadline: "text-lg", textAlignHeadline: "center",
        isVisibleHeader: true, textHeader: td("netVideoHeader"), fontSizeHeader: "text-3xl", textAlignHeader: "center",
        isVisibleText: true, textText: td("netVideoText"), fontSizeText: "text-md", textAlignText: "center",
        buttonSize: "medium", buttonText: td("netVideoButton"), isVisibleButton: true, buttonBorder: true, buttonUnderline: false, buttonAlign: "center",
        videoAspectRatio: "aspect-video", isVisibleVideo: true, videoUrl: "https://youtu.be/sdESKm-nKQE",
      },
      displayName: "Video", custom: { icon: "IconVideo", preview: pv("Video-preview") },
      parent: B, hidden: false, nodes: [],
      linkedNodes: { headline: "net_vd_eb", header: "net_vd_hd", text: "net_vd_tx", button: "net_vd_btn" },
    },
    net_vd_eb: tc(VIDEO, { isVisible: false, text: td("netVideoHeader"), fontSize: "text-lg", textAlign: "center", displayName: "headline", header: true }),
    net_vd_hd: tc(VIDEO, { isVisible: true, text: td("netVideoHeader"), fontSize: "text-3xl", textAlign: "center", displayName: "header", header: true }),
    net_vd_tx: tc(VIDEO, { isVisible: true, text: td("netVideoText"), fontSize: "text-md", textAlign: "center", displayName: "text", header: false }),
    net_vd_btn: btn(VIDEO, { text: td("netVideoButton"), isVisible: true, displayName: "button" }),

    // ── Testimonials (dark surface) ──
    [TESTI]: {
      type: { resolvedName: "Testimonials" }, isCanvas: true,
      props: {
        padding: PAD, distHeadlineHeader: "mb-3 wb-md:mb-4", distHeaderText: "mb-4 wb-md:mb-6", distTextTestimonials: "gap-8 wb-md:gap-12", distTestimonials: "gap-5 wb-md:gap-6", sectionMargin: SEC_MARGIN, isVisible: true, gridColumns: "3", layoutWidth: "100%",
        backgroundColor: DARK,
        isVisibleHeadline: true, textHeadline: td("netTestimonialsEyebrow"), fontSizeHeadline: "text-xs", textAlignHeadline: "center",
        isVisibleHeader: true, textHeader: td("netTestimonialsHeader"), fontSizeHeader: "text-3xl", textAlignHeader: "center",
        isVisibleText: false, textText: td("netTestimonialsHeader"), fontSizeText: "text-md", textAlignText: "center",
      },
      displayName: "Testimonials", custom: { icon: "IconQuote", preview: pv("Testimonials-preview"), allowedComponents: ["TestimonialsChild"] },
      parent: B, hidden: false, nodes: ["net_ts1", "net_ts2", "net_ts3"],
      linkedNodes: { headline: "net_ts_eb", header: "net_ts_hd", text: "net_ts_tx" },
    },
    net_ts_eb: tc(TESTI, { isVisible: true, text: td("netTestimonialsEyebrow"), fontSize: "text-xs", textAlign: "center", displayName: "headline", color: ON_DARK_ACCENT, header: true }),
    net_ts_hd: tc(TESTI, { isVisible: true, text: td("netTestimonialsHeader"), fontSize: "text-3xl", textAlign: "center", displayName: "header", color: "#ffffff", header: true }),
    net_ts_tx: tc(TESTI, { isVisible: false, text: td("netTestimonialsHeader"), fontSize: "text-md", textAlign: "center", displayName: "text", color: ON_DARK, header: false }),
    net_ts1: testimonialChild(TESTI, 0, td),
    net_ts2: testimonialChild(TESTI, 1, td),
    net_ts3: testimonialChild(TESTI, 2, td),

    // ── Blog (pre-reading) — present but hidden by default ──
    [BLOG]: {
      type: { resolvedName: "Blog" }, isCanvas: true,
      props: {
        padding: PAD, distHeadlineHeader: "mb-3 wb-md:mb-4", distHeaderText: "mb-4 wb-md:mb-6", distTextEntries: "gap-8 wb-md:gap-12", distEntries: "gap-5 wb-md:gap-6", sectionMargin: SEC_MARGIN, isVisible: false, gridColumns: "3", layoutWidth: "100%",
        isVisibleHeadline: false, textHeadline: td("netBlogHeader"), fontSizeHeadline: "text-lg", textAlignHeadline: "center",
        isVisibleHeader: true, textHeader: td("netBlogHeader"), fontSizeHeader: "text-3xl", textAlignHeader: "center",
        isVisibleText: true, textText: td("netBlogText"), fontSizeText: "text-md", textAlignText: "center",
      },
      displayName: "Blog", custom: { icon: "IconClick", preview: pv("Blog-preview"), allowedComponents: ["BlogChild"] },
      parent: B, hidden: false, nodes: ["net_bl1", "net_bl2", "net_bl3"],
      linkedNodes: { headline: "net_bl_eb", header: "net_bl_hd", text: "net_bl_tx" },
    },
    net_bl_eb: tc(BLOG, { isVisible: false, text: td("netBlogHeader"), fontSize: "text-lg", textAlign: "center", displayName: "headline", header: true }),
    net_bl_hd: tc(BLOG, { isVisible: true, text: td("netBlogHeader"), fontSize: "text-3xl", textAlign: "center", displayName: "header", header: true }),
    net_bl_tx: tc(BLOG, { isVisible: true, text: td("netBlogText"), fontSize: "text-md", textAlign: "center", displayName: "text", header: false }),
    net_bl1: blogChild(BLOG, 0, td),
    net_bl2: blogChild(BLOG, 1, td),
    net_bl3: blogChild(BLOG, 2, td),

    // ── FaQ ──
    [FAQ]: {
      type: { resolvedName: "FaQ" }, isCanvas: true,
      props: {
        padding: PAD, distHeadlineHeader: "mb-3 wb-md:mb-4", distHeaderText: "mb-4 wb-md:mb-6", distTextEntries: "gap-8 wb-md:gap-12", distEntries: "space-y-3 wb-md:space-y-4", sectionMargin: SEC_MARGIN, isVisible: true, layoutWidth: "100%",
        isVisibleHeadline: false, textHeadline: td("netFaqHeader"), fontSizeHeadline: "text-lg", textAlignHeadline: "center",
        isVisibleHeader: true, textHeader: td("netFaqHeader"), fontSizeHeader: "text-3xl", textAlignHeader: "center",
        isVisibleText: true, textText: td("netFaqText"), fontSizeText: "text-md", textAlignText: "center",
      },
      displayName: "FaQ", custom: { icon: "IconQuestionMark", preview: pv("faqPreview"), allowedComponents: ["FaQChild"] },
      parent: B, hidden: false, nodes: ["net_fq1", "net_fq2", "net_fq3", "net_fq4", "net_fq5", "net_fq6"],
      linkedNodes: { headline: "net_fq_eb", header: "net_fq_hd", text: "net_fq_tx" },
    },
    net_fq_eb: tc(FAQ, { isVisible: false, text: td("netFaqHeader"), fontSize: "text-lg", textAlign: "center", displayName: "headline", header: true }),
    net_fq_hd: tc(FAQ, { isVisible: true, text: td("netFaqHeader"), fontSize: "text-3xl", textAlign: "center", displayName: "header", header: true }),
    net_fq_tx: tc(FAQ, { isVisible: true, text: td("netFaqText"), fontSize: "text-md", textAlign: "center", displayName: "text", header: false }),
    net_fq1: faqChild(FAQ, 0, td),
    net_fq2: faqChild(FAQ, 1, td),
    net_fq3: faqChild(FAQ, 2, td),
    net_fq4: faqChild(FAQ, 3, td),
    net_fq5: faqChild(FAQ, 4, td),
    net_fq6: faqChild(FAQ, 5, td),

    // ── Map (rooftop venue) — present but hidden by default ──
    [MAP]: {
      type: { resolvedName: "Map" }, isCanvas: false,
      props: {
        sectionHeight: 70, contentAlignment: "center", sectionMargin: SEC_MARGIN, layoutWidth: "50%",
        padding: PAD, distHeadlineHeader: "mb-3 wb-md:mb-4", distHeaderText: "mb-4 wb-md:mb-6", distTextButton: "mb-6 wb-md:mb-8", distContentMap: "gap-6 wb-md:gap-10 wb-lg:gap-16", isVisible: false,
        isVisibleHeadline: false, textHeadline: td("netMapHeader"), fontSizeHeadline: "text-lg", textAlignHeadline: "left",
        isVisibleHeader: true, textHeader: td("netMapHeader"), fontSizeHeader: "text-3xl", textAlignHeader: "left",
        isVisibleText: true, textText: td("netMapText"), fontSizeText: "text-md", textAlignText: "left",
        buttonSize: "medium", buttonText: td("netMapButton"), isVisibleButton: true, buttonBorder: true, buttonUnderline: false, buttonAlign: "left",
        mapRounded: "rounded-lg",
        mapZoom: 12, mapType: "m", aspectRatio: "aspect-[4/3]", isVisibleMap: true, mapLocation: td("netMapLocation"),
      },
      displayName: "Map", custom: { icon: "IconMap2", preview: pv("Map-preview") },
      parent: B, hidden: false, nodes: [],
      linkedNodes: { headline: "net_mp_eb", header: "net_mp_hd", text: "net_mp_tx", button: "net_mp_btn" },
    },
    net_mp_eb: tc(MAP, { isVisible: false, text: td("netMapHeader"), fontSize: "text-lg", textAlign: "left", displayName: "headline", header: true }),
    net_mp_hd: tc(MAP, { isVisible: true, text: td("netMapHeader"), fontSize: "text-3xl", textAlign: "left", displayName: "header", header: true }),
    net_mp_tx: tc(MAP, { isVisible: true, text: td("netMapText"), fontSize: "text-md", textAlign: "left", displayName: "text", header: false }),
    net_mp_btn: btn(MAP, { text: td("netMapButton"), displayName: "button" }),

    // ── DataInput (multi-step RSVP) ──
    [DI]: {
      type: { resolvedName: "DataInput" }, isCanvas: false,
      props: {
        padding: PAD, sectionMargin: SEC_MARGIN, isVisible: true, textSpacing: DIST_CARD,
        backgroundColor: "transparent", border: false, borderWidth: "border-2", borderStyle: "border-solid",
        borderSides: ["bottom"], borderRadius: undefined,
        isVisibleHeadline: true, textHeadline: td("netDataHeadline"), fontSizeHeadline: "text-base", textAlignHeadline: "center",
        isVisibleHeader: true, textHeader: td("netDataHeader"), fontSizeHeader: "text-lg", textAlignHeader: "center",
        isVisibleText: true, textText: td("netDataText"), fontSizeText: "text-sm", textAlignText: "center",
        allowUpdate: true,
        multiStepEnabled: true, stepLabels: [td("netStepBasics"), td("netStepProfile"), td("netStepContext"), td("netStepOverview")], multiStepMode: "pages",
        progressBarEnabled: true, progressBarColor: ACCENT,
        overviewEnabled: true, stepSections: [["fields-0"], ["fields-1"], ["fields-2"], []],
      },
      displayName: "DataInput", custom: { icon: "IconForms", preview: pv("DataInput-Preview") },
      parent: B, hidden: false, nodes: [],
      linkedNodes: {
        headline: "net_di_hl", header: "net_di_hd", text: "net_di_tx",
        fieldsGroup0: "net_di_f0", fieldsGroup1: "net_di_f1", fieldsGroup2: "net_di_f2",
        fieldsGroup3: "net_di_f3", fieldsGroup4: "net_di_f4",
        companionsGroup: "net_di_comp", overviewGroup: "net_di_ov", checkboxes: "net_di_cb",
        button: "net_di_btn", backButton: "net_di_back", nextButton: "net_di_next",
      },
    },
    net_di_hl: tc(DI, { isVisible: true, text: td("netDataHeadline"), submittedText: "", isSubmitted: false, enableSubmittedState: true, fontSize: "text-base", textAlign: "center", displayName: "headline", header: true }),
    net_di_hd: tc(DI, { isVisible: true, text: td("netDataHeader"), submittedText: "", isSubmitted: false, enableSubmittedState: true, fontSize: "text-lg", textAlign: "center", displayName: "header", header: true }),
    net_di_tx: tc(DI, { isVisible: true, text: td("netDataText"), submittedText: "", isSubmitted: false, enableSubmittedState: true, fontSize: "text-sm", textAlign: "center", displayName: "text", header: false }),
    net_di_f0: {
      type: { resolvedName: "FieldsGroup" }, isCanvas: true,
      props: { isVisible: true, fieldCount: 4, groupIndex: 0, fieldSpacing: "medium" },
      displayName: "FieldsGroup", custom: { icon: "IconForms", allowedComponents: ["FormInputChild"] },
      parent: DI, hidden: false, nodes: ["net_di_acceptance", "net_di_first", "net_di_last", "net_di_email"], linkedNodes: {},
    },
    // FIRST in the group, and filter-free — the question every other
    // field in this group is gated on. See `acceptanceFi`.
    net_di_acceptance: acceptanceFi("net_di_f0", td("acceptanceState")),
    net_di_first: fi("net_di_f0", { label: td("firstName"), requiredField: true, inputFieldVariable: "firstName", visibilityFilter: ACCEPTANCE_VISIBILITY_FILTER }),
    net_di_last: fi("net_di_f0", { label: td("lastName"), requiredField: true, inputFieldVariable: "lastName", visibilityFilter: ACCEPTANCE_VISIBILITY_FILTER }),
    net_di_email: fi("net_di_f0", { label: td("email"), requiredField: true, inputFieldVariable: "email", gridArea: "col-span-12", visibilityFilter: ACCEPTANCE_VISIBILITY_FILTER }),
    net_di_f1: {
      type: { resolvedName: "FieldsGroup" }, isCanvas: true,
      props: { isVisible: true, fieldCount: 2, groupIndex: 1, fieldSpacing: "medium" },
      displayName: "FieldsGroup", custom: { icon: "IconForms", allowedComponents: ["FormInputChild"] },
      parent: DI, hidden: false, nodes: ["net_di_company", "net_di_role"], linkedNodes: {},
    },
    net_di_company: fi("net_di_f1", { label: td("netFieldCompany"), overwriteType: "STRING", requiredField: false, inputFieldVariable: "company", changeable: true }),
    net_di_role: fi("net_di_f1", { label: td("netFieldRole"), overwriteType: "STRING", requiredField: false, inputFieldVariable: "role", changeable: true }),
    net_di_f2: {
      type: { resolvedName: "FieldsGroup" }, isCanvas: true,
      props: { isVisible: true, fieldCount: 1, groupIndex: 2, fieldSpacing: "medium" },
      displayName: "FieldsGroup", custom: { icon: "IconForms", allowedComponents: ["FormInputChild"] },
      parent: DI, hidden: false, nodes: ["net_di_lookingfor"], linkedNodes: {},
    },
    net_di_lookingfor: fi("net_di_f2", { label: td("netFieldLookingFor"), overwriteType: "STRING", requiredField: false, inputFieldVariable: "lookingFor", multiline: true, gridArea: "col-span-12", changeable: true }),
    net_di_f3: emptyFieldsGroup(DI, 3),
    net_di_f4: emptyFieldsGroup(DI, 4),
    net_di_comp: {
      type: { resolvedName: "CompanionsGroup" }, isCanvas: true,
      props: { isVisible: false, fieldCount: 1, fieldSpacing: "medium", visibilityFilter: ACCEPTANCE_VISIBILITY_FILTER },
      displayName: "CompanionsGroup", custom: { icon: "IconUsers", allowedComponents: ["FormInputChild"] },
      parent: DI, hidden: false, nodes: ["net_di_comp_first"], linkedNodes: {},
    },
    net_di_comp_first: fi("net_di_comp", { label: td("firstName"), requiredField: false, inputFieldVariable: "firstName", gridArea: "col-span-12", changeable: true, type: "companion" }),
    net_di_ov_t: tc("net_di_ov", { isVisible: true, text: td("overviewTitle"), submittedText: "", isSubmitted: false, enableSubmittedState: true, fontSize: "text-base", textAlign: "left", displayName: "overviewTitle", header: true }),
    net_di_ov_x: tc("net_di_ov", { isVisible: true, text: td("overviewText"), submittedText: "", isSubmitted: false, enableSubmittedState: true, fontSize: "text-sm", textAlign: "left", displayName: "overviewText", header: false }),
    net_di_ov: {
      type: { resolvedName: "OverviewGroup" }, isCanvas: true,
      props: { isVisible: false, parentNodeId: DI, stepSections: [["fields-0"], ["fields-1"], ["fields-2"], []], stepLabels: [] },
      displayName: "summaryGroup", custom: { icon: "IconListDetails" },
      parent: DI, hidden: false, nodes: ["net_di_ov_t", "net_di_ov_x"], linkedNodes: {},
    },
    net_di_cb: {
      type: { resolvedName: "Checkboxes" }, isCanvas: false,
      props: { isVisible: true, checkboxes: toCheckboxItems(defaultRegistrationConsents(td)), displayName: "checkboxes" },
      displayName: "Checkboxes", custom: { icon: "IconCheckbox" },
      parent: DI, hidden: false, nodes: [], linkedNodes: {},
    },
    net_di_btn: btn(DI, { linkType: "internal", linkUrl: "", text: td("netDataSubmit"), submittedText: "", isSubmitted: false, enableSubmittedState: true, size: "small", widthMode: "full", buttonType: "submit", margin: BTN_FORM_SP, displayName: "button" }),
    net_di_back: btn(DI, { linkType: "internal", type: "secondary", linkUrl: "", text: td("back"), navigationRole: "back", size: "small", widthMode: "content", isVisible: false, displayName: "backButton" }),
    net_di_next: btn(DI, { linkType: "internal", linkUrl: "", text: td("next"), navigationRole: "next", size: "small", widthMode: "content", isVisible: false, displayName: "nextButton" }),

    // ── Contact ──
    [CONTACT]: {
      type: { resolvedName: "Contact" }, isCanvas: true,
      props: {
        sectionHeight: 70, contentAlignment: "center", infoSection: true, backgroundColor: "transparent",
        padding: PAD, sectionMargin: SEC_MARGIN, textSpacing: "space-y-3 wb-md:space-y-5 wb-lg:space-y-6", isVisible: true,
        infoWidth: "50%", inversed: false,
        isVisibleHeadline: true, textHeadline: td("netContactEyebrow"), fontSizeHeadline: "text-xs", textAlignHeadline: "left",
        isVisibleHeader: true, textHeader: td("netContactHeader"), fontSizeHeader: "text-3xl", textAlignHeader: "left",
        isVisibleText: true, textText: td("netContactText"), fontSizeText: "text-md", textAlignText: "left",
        isSocialVisible: true, socialIcons: [{ platform: "linkedin", url: "https://linkedin.com" }, { platform: "instagram", url: "https://instagram.com" }], socialSize: 24, socialPosition: "left",
        buttonSize: "medium", buttonText: td("netContactButton"), isVisibleButton: true, buttonBorder: true, buttonUnderline: false,
      },
      displayName: "Contact", custom: { icon: "IconForms", preview: pv("Contact-preview") },
      parent: B, hidden: false, nodes: [],
      linkedNodes: {
        headline: "net_ct_eb", header: "net_ct_hd", text: "net_ct_tx",
        firstName: "net_ct_first", lastName: "net_ct_last", eMail: "net_ct_email",
        phoneNumber: "net_ct_phone", Message: "net_ct_msg", button: "net_ct_btn",
      },
    },
    net_ct_eb: tc(CONTACT, { isVisible: true, text: td("netContactEyebrow"), fontSize: "text-xs", textAlign: "left", displayName: "headline", color: ACCENT, header: true }),
    net_ct_hd: tc(CONTACT, { isVisible: true, text: td("netContactHeader"), fontSize: "text-3xl", textAlign: "left", displayName: "header", header: true }),
    net_ct_tx: tc(CONTACT, { isVisible: true, text: td("netContactText"), fontSize: "text-md", textAlign: "left", displayName: "text", header: false }),
    net_ct_first: fi(CONTACT, { label: td("firstName"), overwriteType: "STRING", requiredField: false, inputFieldVariable: "firstName", changeable: false }),
    net_ct_last: fi(CONTACT, { label: td("lastName"), overwriteType: "STRING", requiredField: false, inputFieldVariable: "lastName", changeable: false }),
    net_ct_email: fi(CONTACT, { label: td("email"), overwriteType: "EMAIL", requiredField: true, inputFieldVariable: "email", changeable: false, locked: true }),
    net_ct_phone: fi(CONTACT, { label: td("phoneNumber"), overwriteType: "STRING", requiredField: false, inputFieldVariable: "phone", changeable: false }),
    net_ct_msg: fi(CONTACT, { label: td("messageLabel"), overwriteType: "STRING", requiredField: true, inputFieldVariable: "message", multiline: true, gridArea: "col-span-12", changeable: false, locked: true }),
    net_ct_btn: btn(CONTACT, { linkType: "internal", linkUrl: "", text: td("netContactButton"), widthMode: "full", buttonType: "submit", displayName: "button", fixedNavigation: false }),
  });
};

// ── Meta sections (AppBar + Footer) ───────────────────────────────────

/** Glassy white bar, sky-blue rules, logo + nav + "RSVP sichern" CTA. */
export const networkingAppbar = (td: TranslationFn): string =>
  enc({
    type: { resolvedName: "AppBarSection" }, isCanvas: false,
    props: {
      padding: "p-4 wb-md:p-5 wb-lg:p-6", isVisible: true,
      backgroundColor: "#ffffff",
      border: true, borderColor: "#bae6fd", borderWidth: "border", borderStyle: "border-solid", borderSides: ["bottom"],
      sectionOrder: ["logo", "links", "social"],
      isLogoVisible: true, logoSrc: LOGO, logoWidth: "w-32", logoHeight: "h-10", logoPosition: "left",
      isLinksVisible: true, linksPosition: "center",
      links: [
        { label: td("netLinkProgram"), url: "#net_agenda", linkType: "internal" },
        { label: td("netLinkHosts"), url: "#net_speaker", linkType: "internal" },
        { label: td("netLinkFaq"), url: "#net_faq", linkType: "internal" },
        { label: td("netLinkContact"), url: "#net_contact", linkType: "internal" },
      ],
      linksColor: INK, linksFontSize: "text-sm", linksFontWeight: "font-medium",
      isSocialVisible: false, socialIcons: [], socialPosition: "right", socialSize: 24,
      logoutButtonSize: "small", logoutButtonText: td("netHeroPrimaryButton"),
      logoutButtonBorder: true, logoutButtonBackgroundColor: ACCENT, logoutButtonColor: "#ffffff", logoutButtonBorderColor: ACCENT,
    },
    displayName: "AppBar", custom: { icon: "IconLayoutNavbar" },
    parent: "DCH9WIIT55", hidden: false, nodes: [], linkedNodes: {},
  });

/** Dark glassy footer, sky-blue top rule, four link groups + social + copyright. */
export const networkingFooter = (td: TranslationFn): string =>
  enc({
    type: { resolvedName: "FooterSection" }, isCanvas: false,
    props: {
      padding: "px-4 py-10 wb-md:px-6 wb-lg:px-8", isVisible: true,
      backgroundColor: DARK,
      border: true, borderColor: ACCENT, borderWidth: "border-4", borderStyle: "border-solid", borderSides: ["top"],
      sectionOrder: ["links", "social"],
      isLogoVisible: false, logoSrc: LOGO, logoWidth: "w-32", logoHeight: "h-auto",
      isLinkGroupsVisible: true,
      linkGroups: [
        { title: td("netFooterProgram"), links: [
          { label: td("netLinkProgram"), url: "#net_agenda", linkType: "internal" },
          { label: td("netLinkHosts"), url: "#net_speaker", linkType: "internal" },
          { label: td("netLinkMembers"), url: "/login", linkType: "internal" },
        ] },
        // Event practicalities — Maps + add-to-calendar are EXTERNAL (open in a
        // new tab). URLs are placeholders the organiser edits per event.
        { title: td("netFooterEvent"), links: [
          { label: td("netLinkLocation"), url: "https://www.google.com/maps/search/?api=1&query=Berlin", linkType: "external" },
          { label: td("netLinkCalendar"), url: "https://calendar.google.com/calendar/render?action=TEMPLATE&text=Rooftop+Mixer+Vol.+III", linkType: "external" },
          { label: td("netLinkRsvp"), url: "/register", linkType: "internal" },
        ] },
        { title: td("netFooterSupport"), links: [
          { label: td("netLinkContact"), url: "#net_contact", linkType: "internal" },
          { label: td("netLinkFaq"), url: "#net_faq", linkType: "internal" },
          { label: td("netLinkMediakit"), url: "#net_contact", linkType: "internal" },
        ] },
        { title: td("netFooterLegal"), links: [
          { label: td("netLinkPrivacy"), url: "/privacy", linkType: "internal" },
          { label: td("netLinkImprint"), url: "/imprint", linkType: "internal" },
          { label: td("netLinkTerms"), url: "/terms", linkType: "internal" },
        ] },
      ],
      linksTitleColor: "#ffffff", linksTitleFontSize: "text-sm", linksTitleFontWeight: "font-semibold",
      linksColor: "#94a3b8", linksFontSize: "text-sm", linksFontWeight: "font-normal", linksHoverColor: "hover:font-primary",
      isSocialVisible: true,
      socialIcons: [
        { altText: "linkedin", platform: "linkedin", url: "https://linkedin.com" },
        { altText: "instagram", platform: "instagram", url: "https://instagram.com" },
      ],
      socialColor: "#94a3b8", socialHoverColor: "hover:font-primary", socialSize: 20, socialTitle: td("followUs"),
      isCopyrightVisible: true, copyrightColor: "#64748b", copyrightFontSize: "text-xs", copyrightCompanyName: td("yourCompany"),
    },
    displayName: "Footer", custom: { icon: "IconMenu2" },
    parent: "xaUW2UDZT8", hidden: false, nodes: [], linkedNodes: {},
  });
